/* eslint-disable max-classes-per-file */
export type DataFlowDataType = 'string' | 'integer' | 'boolean' | 'date' | 'timestamp' | 'datetime' | 'any' | 'short'
        | 'double' | 'float' | 'long' | 'decimal' | 'number' | 'integral' | 'fractional' | 'null' | 'array' | 'complex'
        | 'binary' | 'byte' | 'unaryfunction' | 'binaryfunction' | 'string[]' | 'integer[]' | 'boolean[]'| 'date[]'
        | 'timestamp[]' | 'short[]' | 'double[]' | 'float[]' | 'long[]' | 'map' | 'external';

export const udfParameterTypeOptions: DataFlowDataType[] = ['string', 'integer', 'boolean', 'date', 'timestamp', 'any', 'short', 'double', 'float', 'long', 'decimal',
    'number', 'integral', 'fractional', 'binary', 'byte', 'string[]', 'integer[]', 'boolean[]',
    'date[]', 'timestamp[]', 'short[]', 'double[]', 'float[]', 'long[]'];

export interface IDataFlowDataType {
    typeString: DataFlowDataType;
    readonly typeInDsl: string;
    // only used when doing exact type match, like type == 'decimal(10,4)'
    readonly fullyQualifiedType: DataFlowDataType;
    readonly compatibleTypes: DataFlowDataType[];
    format?: string;
}

export abstract class BaseDataFlowDataType implements IDataFlowDataType {
    public abstract typeString: DataFlowDataType;
    public format?: string;
    public get typeInDsl(): string {
        if (this.format) {
            return `${this.typeString} '${this.format.replace(/'/g, '\\\'')}'`;
        }
        return this.typeString;
    }

    public get fullyQualifiedType(): DataFlowDataType {
        return this.typeString;
    }

    public get compatibleTypes(): DataFlowDataType[] {
        return [this.typeString];
    }
}

export abstract class NumberDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'number';

    public get compatibleTypes(): DataFlowDataType[] {
        return [NumberDataFlowType.TYPE, 'byte', 'short', 'integer', 'long', 'float', 'double', 'decimal'];
    }
}

export abstract class BaseIntegerDataFlowType extends NumberDataFlowType {
    public get compatibleTypes(): DataFlowDataType[] {
        return ['byte', 'short', 'integer', 'long'];
    }
}

export class ByteDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'byte';
    public typeString: DataFlowDataType = ByteDataFlowType.TYPE;
}

export class ShortDataFlowType extends BaseIntegerDataFlowType {
    public static TYPE: DataFlowDataType = 'short';
    public typeString: DataFlowDataType = ShortDataFlowType.TYPE;

    public get compatibleTypes(): DataFlowDataType[] {
        return [ShortDataFlowType.TYPE, ByteDataFlowType.TYPE];
    }
}

export class IntegerDataFlowType extends BaseIntegerDataFlowType {
    public static TYPE: DataFlowDataType = 'integer';
    public typeString: DataFlowDataType = IntegerDataFlowType.TYPE;

    public get compatibleTypes(): DataFlowDataType[] {
        return [IntegerDataFlowType.TYPE, ByteDataFlowType.TYPE, ShortDataFlowType.TYPE];
    }
}

export class LongDataFlowType extends BaseIntegerDataFlowType {
    public static TYPE: DataFlowDataType = 'long';
    public typeString: DataFlowDataType = LongDataFlowType.TYPE;

    public get compatibleTypes(): DataFlowDataType[] {
        return [LongDataFlowType.TYPE, ByteDataFlowType.TYPE, ShortDataFlowType.TYPE, IntegerDataFlowType.TYPE];
    }
}

export abstract class BaseFloatDataFlowType extends NumberDataFlowType {
    public get compatibleTypes(): DataFlowDataType[] {
        return ['float', 'double', 'decimal'];
    }
}

export class FloatDataFlowType extends BaseFloatDataFlowType {
    public static TYPE: DataFlowDataType = 'float';
    public typeString: DataFlowDataType = FloatDataFlowType.TYPE;
}

export class DoubleDataFlowType extends BaseFloatDataFlowType {
    public static TYPE: DataFlowDataType = 'double';
    public typeString: DataFlowDataType = DoubleDataFlowType.TYPE;

    public get compatibleTypes(): DataFlowDataType[] {
        return [DoubleDataFlowType.TYPE, FloatDataFlowType.TYPE];
    }
}

export class StringDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'string';
    public typeString: DataFlowDataType = StringDataFlowType.TYPE;
}

export class BooleanDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'boolean';
    public typeString: DataFlowDataType = BooleanDataFlowType.TYPE;
}

export class BinaryDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'binary';
    public typeString: DataFlowDataType = BinaryDataFlowType.TYPE;
}

export class DateDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'date';
    public typeString: DataFlowDataType = DateDataFlowType.TYPE;
}

export class TimeStampDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'timestamp';
    public typeString: DataFlowDataType = TimeStampDataFlowType.TYPE;
}

export class DecimalDataFlowType extends BaseFloatDataFlowType {
    public static TYPE: DataFlowDataType = 'decimal';
    public typeString: DataFlowDataType = DecimalDataFlowType.TYPE;

    // default value is based on spark decimal type
    public precision: number = 10;
    public scale: number = 0;

    public get typeInDsl(): string {
        if (this.format) {
            return `${this.typeString}(${this.precision},${this.scale}) '${this.format}'`;
        }
        return `${this.typeString}(${this.precision},${this.scale})`;
    }

    public get fullyQualifiedType(): DataFlowDataType {
        return this.typeInDsl as DataFlowDataType;
    }

    public get compatibleTypes(): DataFlowDataType[] {
        return [DecimalDataFlowType.TYPE, FloatDataFlowType.TYPE, DoubleDataFlowType.TYPE];
    }
}

export class AnyDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'any';
    public typeString: DataFlowDataType = AnyDataFlowType.TYPE;
}

export class ArrayDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'array';
    public typeString: DataFlowDataType = ArrayDataFlowType.TYPE;
    public get arrayTypeString(): DataFlowDataType {
        return this.arrayType.typeString;
    }

    public get fullyQualifiedType(): DataFlowDataType {
        return `${this.arrayType.typeString}[]` as DataFlowDataType;
    }

    public get typeInDsl(): string {
        return `${this.arrayType.typeInDsl}[]`;
    }

    public get compatibleTypes(): DataFlowDataType[] {
        return this.arrayType.compatibleTypes.map((t) => `${t}[]` as DataFlowDataType);
    }

    constructor(public arrayType: IDataFlowDataType = new AnyDataFlowType()) {
        super();
    }
}

export class MapDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'map';
    public typeString: DataFlowDataType = MapDataFlowType.TYPE;

    public get mapKeyTypeString(): DataFlowDataType {
        return this.mapKeyType.typeString;
    }

    public get mapKeyValueString(): DataFlowDataType {
        return this.mapValueType.typeString;
    }

    public get fullyQualifiedType(): DataFlowDataType {
        return `[${this.mapKeyType.fullyQualifiedType},${this.mapValueType.fullyQualifiedType}]` as DataFlowDataType;
    }

    public get typeInDsl(): string {
        return `[${this.mapKeyType.typeInDsl},${this.mapValueType.typeInDsl}]`;
    }

    public get compatibleTypes(): DataFlowDataType[] {
        const allTypes: DataFlowDataType[] = [];
        this.mapKeyType.compatibleTypes.forEach((k) => {
            this.mapValueType.compatibleTypes.forEach((v) => {
                allTypes.push(`[${k},${v}]` as DataFlowDataType);
            });
        });
        return allTypes;
    }

    constructor(public mapKeyType: IDataFlowDataType = new AnyDataFlowType(), public mapValueType: IDataFlowDataType = new AnyDataFlowType()) {
        super();
    }
}

export class NullDataFlowType extends BaseDataFlowDataType {
    public static TYPE: DataFlowDataType = 'null';
    public typeString: DataFlowDataType = NullDataFlowType.TYPE;
}
